import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "../lib/supabase";
import { logger } from "../lib/logger";
import { orgTodayKey } from "../lib/portalItems";
import type { MonthlyRevenueGoal } from "../types/core";

// =============================================================================
// useMonthlyGoal — Fetch/Save Revenue Goal for a Month
// =============================================================================
// PURPOSE: Lightweight hook for reading and writing monthly revenue goals.
// Used by GoalProgressBar (all roles) and RevenuePlanner (exec/client).
//
// RETURNS: { goal, isLoading, saveGoal, deleteGoal }
// =============================================================================

function getMonthStart(date: Date = new Date()): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-01`;
}

/**
 * Month key ("YYYY-MM-01") for the goal row. An explicit targetMonth wins (the planner
 * picks a month). Otherwise "this month" is the ORG's month when its timezone is known
 * (RULE ZERO: a US client's Oct 31 evening is Nov 1 in Manila), else the browser's.
 */
export function goalMonthKey(targetMonth?: Date, timeZone?: string | null): string {
  if (targetMonth) return getMonthStart(targetMonth);
  return timeZone ? `${orgTodayKey(timeZone).slice(0, 7)}-01` : getMonthStart();
}

export function useMonthlyGoal(
  organizationId: string | null | undefined,
  targetMonth?: Date,
  timeZone?: string | null
) {
  const [goal, setGoal] = useState<MonthlyRevenueGoal | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  const monthStr = goalMonthKey(targetMonth, timeZone);

  const fetchGoal = useCallback(async () => {
    if (!organizationId || !supabase) {
      setGoal(null);
      return;
    }

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setIsLoading(true);

    try {
      const { data, error } = await supabase
        .from("monthly_revenue_goals")
        .select("*")
        .eq("organization_id", organizationId)
        .eq("month", monthStr)
        .maybeSingle();

      if (controller.signal.aborted) return;

      if (error) {
        console.warn("Failed to fetch monthly goal:", error);
        setGoal(null);
      } else {
        setGoal(data);
      }
    } catch (err) {
      if (controller.signal.aborted) return;
      console.warn("Monthly goal fetch failed:", err);
    } finally {
      if (!controller.signal.aborted) {
        setIsLoading(false);
      }
    }
  }, [organizationId, monthStr]);

  useEffect(() => {
    fetchGoal();
    return () => {
      abortRef.current?.abort();
    };
  }, [fetchGoal]);

  const saveGoal = useCallback(
    async (revenueGoal: number, notes?: string) => {
      if (!organizationId || !supabase) return null;

      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return null;

      const { data, error } = await supabase
        .from("monthly_revenue_goals")
        .upsert(
          {
            organization_id: organizationId,
            month: monthStr,
            revenue_goal: revenueGoal,
            notes: notes ?? null,
            created_by: user.id,
            updated_at: new Date().toISOString(),
          },
          { onConflict: "organization_id,month" }
        )
        .select()
        .single();

      if (error) {
        logger.error("Failed to save goal:", error);
        return null;
      }

      setGoal(data);

      // Bridge: sync goal to monthly_forecasts so stagger chart shows this month.
      // Writes the diagonal cell (forecast_made = forecast_for = goal month) for both metrics.
      const forecastMetrics = ["cash_collected", "revenue_closed"] as const;

      // Remove existing diagonal rows for this month (any type) to avoid json_object_agg collision
      const { error: delErr } = await supabase
        .from("monthly_forecasts")
        .delete()
        .eq("organization_id", organizationId)
        .eq("forecast_made_month", monthStr)
        .eq("forecast_for_month", monthStr)
        .in("metric_name", [...forecastMetrics]);

      if (delErr) {
        logger.error("Failed to clean forecast bridge rows:", delErr);
      }

      // Insert fresh bridge rows
      const { error: insErr } = await supabase.from("monthly_forecasts").insert(
        forecastMetrics.map((metric) => ({
          organization_id: organizationId,
          forecast_made_month: monthStr,
          forecast_for_month: monthStr,
          metric_name: metric,
          predicted_value: revenueGoal,
          actual_value: null,
          forecast_type: "goal",
          notes: "Auto-synced from Cash Planner goal",
        }))
      );

      if (insErr) {
        logger.error("Failed to sync goal to forecasts:", insErr);
      }

      // Forward prediction: project this month's goal as next month's forecast
      // Populates upper-right triangle of stagger chart
      const { error: fwdErr } = await supabase.rpc("snapshot_forward_prediction", {
        p_organization_id: organizationId,
        p_source_month: monthStr,
      });

      if (fwdErr) {
        logger.error("Failed to snapshot forward prediction:", fwdErr);
      }

      return data;
    },
    [organizationId, monthStr]
  );

  const deleteGoal = useCallback(async () => {
    if (!goal?.id || !supabase || !organizationId) return;

    const { error } = await supabase
      .from("monthly_revenue_goals")
      .delete()
      .eq("id", goal.id)
      .eq("organization_id", organizationId);

    if (error) {
      logger.error("Failed to delete goal:", error);
      return;
    }

    setGoal(null);

    // Clean up goal-bridge rows from monthly_forecasts (diagonal + forward predictions)
    await supabase
      .from("monthly_forecasts")
      .delete()
      .eq("organization_id", organizationId)
      .eq("forecast_made_month", monthStr)
      .in("forecast_type", ["goal", "forward_prediction"]);
  }, [goal?.id, organizationId, monthStr]);

  return { goal, isLoading, saveGoal, deleteGoal, refetch: fetchGoal };
}
